import React, { useState, useEffect } from 'react';
import { Habit, Milestone } from '../types';
import { ICON_CATEGORIES, COLORS } from '../constants';
import { X, Trophy, Gift, Plus, Trash2, CalendarDays, Clock, Repeat, Star, Infinity } from 'lucide-react';

interface HabitFormProps {
  initialData?: Habit;
  onSave: (habit: Partial<Habit>) => void;
  onClose: () => void;
  onDelete?: (id: string) => void;
}

const WEEK_DAYS = [
  { val: 1, label: '一' },
  { val: 2, label: '二' },
  { val: 3, label: '三' },
  { val: 4, label: '四' },
  { val: 5, label: '五' },
  { val: 6, label: '六' },
  { val: 0, label: '日' },
];

export const HabitForm: React.FC<HabitFormProps> = ({ initialData, onSave, onClose, onDelete }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [selectedIcon, setSelectedIcon] = useState(initialData?.icon || 'Sun');
  const [selectedColor, setSelectedColor] = useState(initialData?.color || COLORS[0]);

  // Icon Category State
  const [activeCategory, setActiveCategory] = useState<string>('common');

  // Frequency State
  // Map legacy 'workdays'/'custom' to 'weekly' or 'interval' if needed
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'interval'>(
    (initialData?.frequency === 'workdays' || initialData?.frequency === 'custom')
      ? 'weekly'
      : (initialData?.frequency as any) || 'daily'
  );

  const [targetDays, setTargetDays] = useState<number[]>(initialData?.targetDays || [0, 1, 2, 3, 4, 5, 6]);
  const [intervalDays, setIntervalDays] = useState<number>(initialData?.intervalDays || 2);

  const [reminderTime, setReminderTime] = useState(initialData?.reminderTime || '');

  // Period Settings
  const [isPermanent, setIsPermanent] = useState<boolean>(initialData?.isPermanent ?? true);
  const [startDate, setStartDate] = useState<string>(initialData?.startDate || '');
  const [endDate, setEndDate] = useState<string>(initialData?.endDate || '');

  // Points System
  const [basePoints, setBasePoints] = useState<number>(initialData?.basePoints || 10);
  const [streakBonusThreshold, setStreakBonusThreshold] = useState<number>(initialData?.streakBonusThreshold || 0);

  // Milestones State
  const [milestones, setMilestones] = useState<Milestone[]>(initialData?.milestones || []);



  const handleFrequencyChange = (type: 'daily' | 'weekly' | 'interval') => {
    setFrequency(type);
    switch (type) {
      case 'daily':
        setTargetDays([0, 1, 2, 3, 4, 5, 6]);
        break;
      case 'weekly':
        // If switching to weekly, default to current day if empty, or keep existing selection
        if (targetDays.length === 7) {
          // If was daily, switch to just today to encourage selection
          setTargetDays([new Date().getDay()]);
        }
        break;
      case 'interval':
        // Default to every 2 days
        if (!intervalDays) setIntervalDays(2);
        break;
    }
  };

  const toggleDay = (dayVal: number) => {
    setTargetDays(prev => {
      if (prev.includes(dayVal)) {
        // Don't allow empty days for weekly
        if (prev.length === 1) return prev;
        return prev.filter(d => d !== dayVal);
      }
      return [...prev, dayVal];
    });
  };

  const handleAddMilestone = () => {
    setMilestones([...milestones, { target: 7, reward: '' }]);
  };

  const handleRemoveMilestone = (index: number) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const handleUpdateMilestone = (index: number, field: keyof Milestone, value: string | number) => {
    const newMilestones = [...milestones];
    newMilestones[index] = { ...newMilestones[index], [field]: value };
    setMilestones(newMilestones);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    // Filter out empty milestones and sort by target days
    const cleanMilestones = milestones
      .filter(m => m.target > 0)
      .sort((a, b) => a.target - b.target);

    onSave({
      ...initialData,
      title,
      description,
      icon: selectedIcon,
      color: selectedColor,
      frequency,
      targetDays: targetDays,
      intervalDays: frequency === 'interval' ? intervalDays : undefined,
      reminderTime: reminderTime,
      isPermanent: isPermanent,
      startDate: isPermanent ? undefined : startDate,
      endDate: isPermanent ? undefined : endDate,
      streak: initialData?.streak || 0,
      completedDates: initialData?.completedDates || [],
      milestones: cleanMilestones,
      logs: initialData?.logs || [],
      basePoints: basePoints,
      streakBonusThreshold: streakBonusThreshold,
      pointsLog: initialData?.pointsLog || {}
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
          <h2 className="text-lg font-bold text-slate-800 dark:text-white">
            {initialData ? '编辑习惯' : '新建习惯'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto no-scrollbar space-y-6">
          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">名称</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例如：晨跑"
              className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">描述 (可选)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="例如：绕公园跑3公里"
              className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Reminder Time */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <div className="flex items-center gap-1">
                <Clock size={16} /> 提醒时间
              </div>
            </label>
            <div className="flex gap-2">
              <input
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                className="flex-1 bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
              {reminderTime && (
                <button
                  type="button"
                  onClick={() => setReminderTime('')}
                  className="px-4 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-xs"
                >
                  清除
                </button>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1 ml-1">设置后，若到点未打卡，习惯卡片将显示超时提示。</p>
          </div>

          {/* Frequency Section */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">频率</label>
            <div className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl flex flex-wrap gap-1">
              {[
                { id: 'daily', label: '每天' },
                { id: 'weekly', label: '每周' },
                { id: 'interval', label: '按周期' }
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleFrequencyChange(opt.id as any)}
                  className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium transition-all ${frequency === opt.id ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm font-bold' : 'text-slate-500'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Weekly Selector (Specific Days) */}
            {frequency === 'weekly' && (
              <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700 animate-fade-in">
                <p className="text-xs text-slate-400 mb-3 flex items-center gap-1">
                  <CalendarDays size={12} /> 选择打卡日
                </p>
                <div className="flex justify-between gap-1">
                  {WEEK_DAYS.map((d) => {
                    const isSelected = targetDays.includes(d.val);
                    return (
                      <button
                        key={d.val}
                        type="button"
                        onClick={() => toggleDay(d.val)}
                        className={`
                                        w-8 h-8 rounded-full text-xs font-bold transition-all
                                        ${isSelected
                            ? 'bg-primary-500 text-white shadow-md shadow-primary-200 dark:shadow-none'
                            : 'bg-white dark:bg-slate-700 text-slate-400 border border-slate-200 dark:border-slate-600'}
                                    `}
                      >
                        {d.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Interval Selector */}
            {frequency === 'interval' && (
              <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700 animate-fade-in">
                <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                  <Repeat size={12} /> 设置循环周期
                </p>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600 dark:text-slate-300">每</span>
                  <input
                    type="number"
                    min="2"
                    max="365"
                    value={intervalDays}
                    onChange={(e) => setIntervalDays(parseInt(e.target.value) || 2)}
                    className="w-16 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg py-2 text-sm font-bold"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300">天打卡一次</span>
                </div>
              </div>
            )}
          </div>

          {/* Period Settings */}
          <div className="bg-cyan-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-cyan-100 dark:border-slate-700">
            <div className="flex items-center space-x-2 mb-3 text-cyan-600 dark:text-cyan-400">
              <CalendarDays size={18} />
              <span className="font-bold text-sm">任务周期</span>
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsPermanent(true)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    isPermanent
                      ? 'bg-cyan-500 text-white shadow-md'
                      : 'bg-white dark:bg-slate-700 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-600'
                  }`}
                >
                  <Infinity size={16} /> 永久
                </button>
                <button
                  type="button"
                  onClick={() => setIsPermanent(false)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    !isPermanent
                      ? 'bg-cyan-500 text-white shadow-md'
                      : 'bg-white dark:bg-slate-700 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-600'
                  }`}
                >
                  自定周期
                </button>
              </div>

              {!isPermanent && (
                <div className="space-y-3 animate-fade-in">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      开始日期
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      结束日期
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-800 dark:text-white"
                    />
                  </div>
                  {startDate && endDate && (
                    <p className="text-xs text-slate-400 text-center">
                      周期: {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} 天
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Milestones Section */}
          <div className="bg-indigo-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-indigo-100 dark:border-slate-700">
            <div className="flex items-center justify-between mb-3 text-indigo-600 dark:text-indigo-400">
              <div className="flex items-center space-x-2">
                <Trophy size={18} />
                <span className="font-bold text-sm">阶段目标 & 奖励</span>
              </div>
              <button
                type="button"
                onClick={handleAddMilestone}
                className="p-1 hover:bg-indigo-100 dark:hover:bg-slate-700 rounded transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>

            <div className="space-y-3">
              {milestones.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-2 italic">添加目标，激励自己坚持下去！</p>
              )}
              {milestones.map((milestone, index) => (
                <div key={index} className="flex items-start gap-2 animate-fade-in">
                  <div className="w-20 flex-shrink-0">
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        value={milestone.target}
                        onChange={(e) => handleUpdateMilestone(index, 'target', parseInt(e.target.value) || 0)}
                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-2 text-sm text-center font-bold text-slate-800 dark:text-white"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">天</span>
                    </div>
                  </div>
                  <div className="flex-1 relative">
                    <Gift size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={milestone.reward}
                      onChange={(e) => handleUpdateMilestone(index, 'reward', e.target.value)}
                      placeholder="输入奖励..."
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg pl-8 pr-2 py-2 text-sm text-slate-800 dark:text-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveMilestone(index)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>


          {/* Points System Config */}
          <div className="bg-amber-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-amber-100 dark:border-slate-700">
            <div className="flex items-center space-x-2 mb-3 text-amber-600 dark:text-amber-400">
              <Star size={18} />
              <span className="font-bold text-sm">积分奖励</span>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  单次打卡基础分
                </label>
                <input
                  type="number"
                  min="0"
                  value={basePoints}
                  onChange={(e) => setBasePoints(parseInt(e.target.value) || 0)}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  连续打卡奖励阈值 (天)
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    min="0"
                    value={streakBonusThreshold}
                    onChange={(e) => setStreakBonusThreshold(parseInt(e.target.value) || 0)}
                    className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-800 dark:text-white"
                  />
                  <span className="text-xs text-slate-400 shrink-0">0 为不启用</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  连续打卡达到此天数后，只要不中断，每次打卡都获得双倍积分。
                </p>
              </div>
            </div>
          </div>

          {/* Icon Selection with Horizontal Tabs */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">图标</label>
            {/* Horizontal Category Tabs */}
            <div className="flex gap-1 overflow-x-auto no-scrollbar pb-2 mb-2">
              {ICON_CATEGORIES.map((category) => {
                const isActive = activeCategory === category.id;
                const hasSelectedIcon = category.icons.some(icon => icon.name === selectedIcon);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveCategory(category.id)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-primary-500 text-white'
                        : hasSelectedIcon
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
            {/* Icons Grid for Active Category */}
            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <div className="flex flex-wrap gap-2">
                {ICON_CATEGORIES.find(cat => cat.id === activeCategory)?.icons.map((icon) => {
                  const Icon = icon.component;
                  const isSelected = selectedIcon === icon.name;
                  const iconColor = icon.color || '#6B7280';
                  return (
                    <button
                      key={icon.name}
                      type="button"
                      onClick={() => setSelectedIcon(icon.name)}
                      title={icon.name}
                      className={`p-2.5 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-primary-500 text-white shadow-md shadow-primary-200 dark:shadow-none scale-110'
                          : 'bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600'
                      }`}
                    >
                      <Icon size={20} color={isSelected ? 'white' : iconColor} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">颜色</label>
            <div className="flex flex-wrap gap-3">
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className={`w-8 h-8 rounded-full ${color} transition-transform ${selectedColor === color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : 'hover:scale-110'}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900 flex gap-3">
          {initialData && onDelete && (
            <button
              onClick={() => {
                if (confirm('确定要删除这个习惯吗？')) {
                  onDelete(initialData.id);
                  onClose();
                }
              }}
              className="px-4 py-3 rounded-xl bg-red-100 text-red-600 font-medium hover:bg-red-200 transition-colors"
            >
              删除
            </button>
          )}
          <button
            onClick={handleSubmit}
            className="flex-1 bg-slate-900 dark:bg-white dark:text-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-all active:scale-95 shadow-lg shadow-slate-200 dark:shadow-none"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};